import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";
import {
  buildFirebaseSessionId,
  resetWaitingRoom,
  skipWaitingRoom,
  subscribeWaitingRoom,
} from "../../../utils/canvasFirebase";

const THANK_YOU_MSG_MAX_CHARS = 200;
const WAITING_TIMER_MIN_MINUTES = 0;
const WAITING_TIMER_MAX_MINUTES = 60;
const WAITING_TIMER_DEFAULT_MINUTES = 5;
const WAITING_TIMER_MIN_SECONDS = 0;
const WAITING_TIMER_MAX_SECONDS = 59;
const WAITING_TIMER_DEFAULT_SECONDS = 0;

function clampWaitingTimerMinutes(value, fallback = WAITING_TIMER_DEFAULT_MINUTES) {
  const val = Math.floor(Number(value));
  if (!Number.isFinite(val)) {
    return Math.min(Math.max(fallback, WAITING_TIMER_MIN_MINUTES), WAITING_TIMER_MAX_MINUTES);
  }
  return Math.min(Math.max(val, WAITING_TIMER_MIN_MINUTES), WAITING_TIMER_MAX_MINUTES);
}

function clampWaitingTimerSeconds(value, fallback = WAITING_TIMER_DEFAULT_SECONDS) {
  const val = Math.floor(Number(value));
  if (!Number.isFinite(val)) {
    return Math.min(Math.max(fallback, WAITING_TIMER_MIN_SECONDS), WAITING_TIMER_MAX_SECONDS);
  }
  return Math.min(Math.max(val, WAITING_TIMER_MIN_SECONDS), WAITING_TIMER_MAX_SECONDS);
}

/** Category order from theme payload (themeset + DB merge from fetchThemeData). */
function getThankyouCategoryKeys(themeData) {
  if (!themeData || typeof themeData !== "object") return [];
  const d = themeData.default_cat;
  const f = themeData.full_default_cat;
  if (Array.isArray(d) && d.length > 0) return d.map(String);
  if (Array.isArray(f) && f.length > 0) return f.map(String);
  const tm = themeData.thankyou_message;
  if (tm && typeof tm === "object" && !Array.isArray(tm)) {
    return Object.keys(tm);
  }
  return [];
}

/** Themeset + API only use plain strings per category (no score-band objects). */
function migrateThankyouMessageToCategoryStrings(raw, categoryKeys) {
  const o = Object.fromEntries(categoryKeys.map((k) => [k, ""]));
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return o;
  for (const k of categoryKeys) {
    if (typeof raw[k] === "string") o[k] = raw[k];
  }
  return o;
}

function clampThankyouCategoryStrings(msg, categoryKeys, maxLen) {
  const o = { ...msg };
  for (const k of categoryKeys) {
    if (typeof o[k] === "string" && o[k].length > maxLen) o[k] = o[k].slice(0, maxLen);
  }
  return o;
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status, data } = useSelector((state) => state.theme);

  const [customTextThankYouPage, setCustomTextThankYouPage] = useState("");
  const [rules, setRules] = useState([""]);
  const [thankyouToggle, setThankyouToggle] = useState(false);
  const [thankyouMessage, setThankyouMessage] = useState({});
  const [textColor, setTextColor] = useState("#ffffff");
  const [buttonBackgroundColor, setButtonBackgroundColor] = useState("#6845D3");
  const [boxTextColor, setBoxTextColor] = useState("#000000");
  const [wrongPoints, setWrongPoints] = useState("1");
  const [waitingTimerMinutes, setWaitingTimerMinutes] = useState(WAITING_TIMER_DEFAULT_MINUTES);
  const [waitingTimerSeconds, setWaitingTimerSeconds] = useState(WAITING_TIMER_DEFAULT_SECONDS);
  const [resettingWaitingRoom, setResettingWaitingRoom] = useState(false);
  const [skippingWaitingRoom, setSkippingWaitingRoom] = useState(false);
  const [waitingRoomStarted, setWaitingRoomStarted] = useState(false);

  // Load the theme once on mount so a browser refresh shows the saved values instead of local
  // defaults. A successful fetch rewrites state.theme.currentTheme, so currentTheme must NOT be
  // a dependency (it re-ran this effect and fetched everything a second time), and when Home
  // already loaded the theme there is nothing to fetch.
  useEffect(() => {
    if (adminToken && status !== 'succeeded') dispatch(fetchThemeData({ themeId: currentTheme }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, adminToken]);

  const firebaseSessionId = useMemo(
    () => (organizationId && sessionId ? buildFirebaseSessionId(organizationId, sessionId) : null),
    [organizationId, sessionId]
  );

  // Tracks whether this live session's lobby countdown has been started yet, so the
  // Start/Restart button can label itself correctly and Skip only shows once relevant.
  useEffect(() => {
    if (!firebaseSessionId) return undefined;
    return subscribeWaitingRoom(firebaseSessionId, (waitingRoomData) => {
      setWaitingRoomStarted(Boolean(waitingRoomData?.startedAt));
    });
  }, [firebaseSessionId]);

  useEffect(() => {
    if (!data) return;
    const categoryKeys = getThankyouCategoryKeys(data);
    setCustomTextThankYouPage(
      data.custom_text_thank_you_page != null ? String(data.custom_text_thank_you_page) : ""
    );
    setRules(Array.isArray(data.rules) && data.rules.length ? data.rules : [""]);
    setThankyouToggle(data.thankyou_toggle === "true" || data.thankyou_toggle === true);
    setThankyouMessage(
      clampThankyouCategoryStrings(
        migrateThankyouMessageToCategoryStrings(data.thankyou_message, categoryKeys),
        categoryKeys,
        THANK_YOU_MSG_MAX_CHARS
      )
    );
    setTextColor(data.text_color ?? "#ffffff");
    setButtonBackgroundColor(
      data.button_background_color ?? data.box_bg_color ?? "#6845D3"
    );
    setBoxTextColor(data.box_text_color ?? "#000000");
    setWrongPoints(String(data.wrong_points ?? "1"));
    setWaitingTimerMinutes(clampWaitingTimerMinutes(data.waiting_timer_minutes));
    setWaitingTimerSeconds(clampWaitingTimerSeconds(data.waiting_timer_seconds));
  }, [data]);

  const categoryKeys = data ? getThankyouCategoryKeys(data) : [];

  const handleThankyouCategoryChange = (cat, val) => {
    if (val.length > THANK_YOU_MSG_MAX_CHARS) {
      void Swal.fire({
        icon: "warning",
        title: "Max limit crossed",
        text: `Thank you messages can be at most ${THANK_YOU_MSG_MAX_CHARS} characters each.`,
      });
      val = val.slice(0, THANK_YOU_MSG_MAX_CHARS);
    }
    setThankyouMessage((prev) => ({ ...prev, [cat]: val }));
  };

  const handleRuleChange = (idx, val) => {
    if (val.length > 200) {
      Swal.fire("Limit Exceeded", "Rules can be at most 200 characters.", "warning");
      val = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire("Limit Reached", "You can only add up to 6 rules.", "warning");
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire("At Least One Rule Required", "You must have at least one rule.", "warning");
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    const filledRules = rules.map((r) => r.trim()).filter(Boolean);
    if (filledRules.length === 0) {
      Swal.fire({
        title: "Validation",
        text: "Add at least one game rule. Rules cannot be left blank.",
        icon: "error",
      });
      return;
    }

    if (waitingTimerMinutes * 60 + waitingTimerSeconds <= 0) {
      Swal.fire("Invalid timer", "Waiting timer must be at least 1 second.", "warning");
      return;
    }

    const msgOut = { ...thankyouMessage };
    for (const k of categoryKeys) {
      if (!(k in msgOut)) msgOut[k] = "";
    }

    const payload = {
      data: {
        custom_text_thank_you_page: customTextThankYouPage,
        rules: filledRules,
        thankyou_toggle: thankyouToggle ? "true" : "false",
        thankyou_message: msgOut,
        text_color: textColor,
        button_background_color: buttonBackgroundColor,
        box_bg_color: buttonBackgroundColor,
        box_text_color: boxTextColor,
        wrong_points: String(wrongPoints),
        waiting_timer_minutes: String(clampWaitingTimerMinutes(waitingTimerMinutes)),
        waiting_timer_seconds: String(clampWaitingTimerSeconds(waitingTimerSeconds)),
      },
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", "Theme settings saved successfully.", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    }
  };

  const handleResetWaitingRoom = async () => {
    if (!firebaseSessionId) {
      Swal.fire("No live session", "Open this page from a live event link to start its waiting room.", "info");
      return;
    }
    const totalSeconds =
      clampWaitingTimerMinutes(waitingTimerMinutes) * 60 + clampWaitingTimerSeconds(waitingTimerSeconds);
    if (totalSeconds <= 0) {
      Swal.fire("Invalid timer", "Waiting timer must be at least 1 second.", "warning");
      return;
    }
    try {
      setResettingWaitingRoom(true);
      await resetWaitingRoom(firebaseSessionId, totalSeconds);
      Swal.fire(
        waitingRoomStarted ? "Restarted" : "Started",
        `Waiting timer ${waitingRoomStarted ? "restarted" : "started"} for everyone in the lobby.`,
        "success"
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to start the waiting timer.", "error");
    } finally {
      setResettingWaitingRoom(false);
    }
  };

  const handleSkipWaitingRoom = async () => {
    if (!firebaseSessionId) {
      Swal.fire("No live session", "Open this page from a live event link to skip its waiting room.", "info");
      return;
    }
    const confirmResult = await Swal.fire({
      title: "Skip the waiting room?",
      text: "Everyone currently in the lobby will be sent straight to the canvas activity now.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Skip Now",
    });
    if (!confirmResult.isConfirmed) return;
    try {
      setSkippingWaitingRoom(true);
      await skipWaitingRoom(firebaseSessionId);
      Swal.fire("Skipped", "Everyone in the lobby is moving to the canvas now.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to skip the waiting timer.", "error");
    } finally {
      setSkippingWaitingRoom(false);
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-center">
              <div className="admin-tab-container" data-tour="rules-game-rules">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game rules
                </div>
                <h3 className="custom-rules-title">Rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <textarea
                      className="rule-input rule-textarea"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                      rows={3}
                    />
                    <button className="remove-btn" onClick={() => handleRemoveRule(i)}>
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <button className="add-btn" onClick={handleAddRule}>
                    + Add New Rule
                  </button>
                  <div className="code">
                    Max 6 rules. At least 1 required. 200 characters per rule.
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container" data-tour="rules-colors">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colors
                </div>
                <div className="pallet-holder">
                  {[
                    { key: "text", label: "Text color", value: textColor, set: setTextColor },
                    {
                      key: "button_bg",
                      label: "Button background color",
                      value: buttonBackgroundColor,
                      set: setButtonBackgroundColor,
                    },
                    {
                      key: "box_text",
                      label: "Box text color",
                      value: boxTextColor,
                      set: setBoxTextColor,
                    },
                  ].map(({ key, label, value, set }) => (
                    <div className="pallet-row pallet-row--label-left" key={key}>
                      <label>{label}</label>
                      <input
                        type="color"
                        className="pallet-swatch"
                        value={value || "#000000"}
                        onChange={(e) => set(e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="admin-tab-container" data-tour="rules-live-session">
            <div className="admin-tab-title">
              <i className="fa-solid fa-hourglass-half"></i> &nbsp; Live Session Controls
            </div>
            <div className="pallet-holder">
              <div className="pallet-row pallet-row--label-left">
                <label>Waiting Timer</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="number"
                    className="rule-input"
                    style={{ width: 90 }}
                    min={WAITING_TIMER_MIN_MINUTES}
                    max={WAITING_TIMER_MAX_MINUTES}
                    value={waitingTimerMinutes}
                    onChange={(e) => setWaitingTimerMinutes(e.target.value)}
                    onBlur={(e) => setWaitingTimerMinutes(clampWaitingTimerMinutes(e.target.value, waitingTimerMinutes))}
                  />
                  <span style={{ fontSize: 13, color: "#6c757d" }}>min</span>
                  <input
                    type="number"
                    className="rule-input"
                    style={{ width: 90 }}
                    min={WAITING_TIMER_MIN_SECONDS}
                    max={WAITING_TIMER_MAX_SECONDS}
                    value={waitingTimerSeconds}
                    onChange={(e) => setWaitingTimerSeconds(e.target.value)}
                    onBlur={(e) => setWaitingTimerSeconds(clampWaitingTimerSeconds(e.target.value, waitingTimerSeconds))}
                  />
                  <span style={{ fontSize: 13, color: "#6c757d" }}>sec</span>
                </div>
              </div>
              <div className="code" style={{ marginBottom: 12 }}>
                How long players wait in the lobby before a Live-mode activity starts. Save this page to apply it to future waiting rooms.
              </div>
              <div className="code" style={{ marginBottom: 12 }}>
                Status: {waitingRoomStarted ? "Timer running for this live session's lobby." : "Not started yet — players in the lobby are waiting for you to start it."}
              </div>
              <div className="rules-button-container" style={{ gap: 10 }}>
                <button
                  className="add-btn"
                  type="button"
                  onClick={handleResetWaitingRoom}
                  disabled={resettingWaitingRoom}
                >
                  {resettingWaitingRoom
                    ? "Starting…"
                    : waitingRoomStarted
                      ? "Restart Timer"
                      : "Start Timer"}
                </button>
                <button
                  className="remove-btn"
                  type="button"
                  style={{ width: "auto", padding: "8px 16px", borderRadius: 6 }}
                  onClick={handleSkipWaitingRoom}
                  disabled={skippingWaitingRoom}
                >
                  {skippingWaitingRoom ? "Skipping…" : "Skip Timer"}
                </button>
              </div>
              <div className="code" style={{ marginBottom: 12 }}>
                Start begins the countdown (from the duration above) for everyone currently in the lobby of THIS live session — players see nothing counting down until you do. Skip sends everyone straight to the canvas activity immediately.
              </div>
            </div>
          </div>
          <div className="rules-action-holder" data-tour="rules-save">
            <button
              className="save-and-continue"
              onClick={handleSubmit}
              disabled={status === "loading"}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
